import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import FiIcons from '../fiIconMap';
import LayoutDefault from '../layouts/LayoutDefault';
import RouteSeo from '../../../seo/RouteSeo';
import { MARKETING_PAGE_META } from '../../../data/marketingPageMeta';
import '../assets/scss/style.scss';

const GUIDE_CARDS = [
    {
        path: '/guides/connect-whatsapp',
        title: 'Connect WhatsApp',
        description: 'Connect WhatsApp to send messages and voice notes to Luckynote.'
    },
    {
        path: '/guides/connect-telegram',
        title: 'Connect Telegram',
        description: 'Connect the Luckynote Telegram bot to save messages and voice notes.'
    },
    {
        path: '/guides/save-from-instagram',
        title: 'Save from Instagram',
        description:
            'Keep recipes, references, reels, screenshots, and copied links before they disappear into the scroll.'
    },
    {
        path: '/guides/save-from-tiktok',
        title: 'Save from TikTok',
        description:
            'Keep recipe clips, tutorials, screenshots, and links in one searchable place instead of trusting memory.'
    },
    {
        path: '/guides/save-from-x',
        title: 'Save from X',
        description:
            'Save posts, threads, screenshots, and links from X with the share sheet, a pasted link, or the browser extension.'
    },
    {
        path: '/guides/save-x-threads',
        title: 'Save full X threads',
        description:
            'Keep the whole thread in one searchable save instead of losing the context behind a single bookmarked post.'
    },
    {
        path: '/guides/save-from-whatsapp',
        title: 'Save from WhatsApp',
        description:
            'Forward or share the useful message, file, screenshot, or link into Luckynote before it gets buried in chat history.'
    },
    {
        path: '/guides/save-from-web',
        title: 'Save from the web',
        description:
            'Use the browser extension, mobile share sheet, or pasted links to turn useful pages into durable saves.'
    },
    {
        path: '/guides/save-links-for-school',
        title: 'Save links for school',
        description:
            'Keep research sources, course pages, screenshots, and assignment details with the context you need when it is time to study or write.'
    },
    {
        path: '/guides/save-screenshots',
        title: 'Save screenshots',
        description:
            'Move important screenshots out of the camera roll and into a place where the text inside them becomes searchable.'
    },
    {
        path: '/guides/save-pdfs-and-files',
        title: 'Save PDFs and files',
        description:
            'Keep documents and attachments with the notes, reminders, and screenshots they belong to.'
    }
];

// Newest lastmod first. Missing lastmod always sorts last. Equal dates keep
// their existing relative order (Array.prototype.sort is stable), preserving
// the curated GUIDE_CARDS ordering as a tie-break.
const byLastmodDesc = (a, b) => {
    if (!a.lastmod && !b.lastmod) return 0;
    if (!a.lastmod) return 1;
    if (!b.lastmod) return -1;
    if (a.lastmod > b.lastmod) return -1;
    if (a.lastmod < b.lastmod) return 1;
    return 0;
};

const GuidesPage = () => {
    const guides = GUIDE_CARDS.map((guide) => {
        const page = MARKETING_PAGE_META.find((candidate) => candidate.path === guide.path);
        return {
            path: guide.path,
            title: guide.title,
            description: guide.description,
            icon: page?.icon,
            color: page?.iconColor,
            background: page?.iconBackground,
            lastmod: page?.lastmod
        };
    }).filter((guide) => guide.icon)
        .sort(byLastmodDesc);

    return (
        <>
            <RouteSeo
                title="How to Get Anything into Luckynote | Luckynote"
                description="Step-by-step guides for saving posts, full X threads, links, screenshots, PDFs, and files into Luckynote."
                canonical="https://luckynote.io/guides"
            />
            <LayoutDefault>
                <DarkSection>
                    <PageShell>
                        <HeroSection>
                            <HeroTitle>Get anything into Luckynote</HeroTitle>
                            <HeroDescription>
                                Start with the capture surface you already use. Each guide shows the durable
                                path into Luckynote without inventing a new habit first.
                            </HeroDescription>
                        </HeroSection>

                        <CardGrid>
                            {guides.map((guide) => {
                                const IconComponent = FiIcons[guide.icon];
                                return (
                                    <GuideCard key={guide.path} to={guide.path}>
                                        <IconBadge $color={guide.color} $background={guide.background}>
                                            <IconComponent size={22} />
                                        </IconBadge>
                                        <CardTitle>{guide.title}</CardTitle>
                                        <CardBody>{guide.description}</CardBody>
                                        <CardCta>
                                            Open guide
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </CardCta>
                                    </GuideCard>
                                );
                            })}
                        </CardGrid>

                        <FooterNote>
                            Want the broader positioning instead? See the <Link to="/use-cases">use cases</Link>,
                            compare options on <Link to="/compare">compare</Link>, or browse{' '}
                            <Link to="/message-yourself">message-yourself apps</Link>.
                        </FooterNote>
                    </PageShell>
                </DarkSection>
            </LayoutDefault>
        </>
    );
};

export default GuidesPage;

const PAPER = '#fcf9f7';
const PAPER_DEEP = '#f6efe7';

const DarkSection = styled.div`
    background:
        radial-gradient(circle at 14% 4%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 26rem),
        linear-gradient(180deg, ${PAPER} 0%, ${PAPER_DEEP} 56%, ${PAPER} 100%);
    min-height: 100vh;
`;

const PageShell = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 96px 20px 96px;
`;

const HeroSection = styled.section`
    padding: 0 0 56px;
    text-align: center;
`;

const HeroTitle = styled.h1`
    margin: 0 0 16px;
    font-size: clamp(2rem, 4vw, 3.2rem);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: #14110f;
`;

const HeroDescription = styled.p`
    margin: 0 auto;
    font-size: 1.1rem;
    line-height: 1.7;
    color: rgba(20, 17, 15, 0.66);
    max-width: 42rem;
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
`;

const GuideCard = styled(Link)`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 28px 26px;
    border-radius: 14px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    background: rgba(255, 255, 255, 0.58);
    text-decoration: none;
    backdrop-filter: blur(12px);
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.88);
        border-color: rgba(20, 17, 15, 0.22);
        box-shadow: 0 18px 42px rgba(59, 46, 35, 0.16);
        transform: translateY(-3px);
    }
`;

const IconBadge = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $background }) => $background};
    color: ${({ $color }) => $color};
    position: relative;

    img,
    svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

const CardTitle = styled.h3`
    margin: 0;
    font-size: 1.15rem;
    color: #14110f;
`;

const CardBody = styled.p`
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.65;
    color: rgba(20, 17, 15, 0.62);
    flex-grow: 1;
`;

const CardCta = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #2b88d9;

    svg {
        transition: transform 0.15s ease;
    }

    ${GuideCard}:hover & svg {
        transform: translateX(3px);
    }
`;

const FooterNote = styled.p`
    margin: 48px auto 0;
    text-align: center;
    font-size: 0.95rem;
    color: rgba(20, 17, 15, 0.62);

    a {
        color: #2b88d9;
        font-weight: 600;
    }
`;
